import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { DayPicker } from 'react-day-picker';
import { format, parse, isValid, startOfDay } from 'date-fns';
import { cn } from '@/lib/utils';

export interface DatePickerProps {
value: string;
onChange: (value: string) => void;
disabled?: boolean;
error?: string;
className?: string;
placeholder?: string;
minDate?: Date;
defaultToToday?: boolean;
compact?: boolean;
onBlur?: () => void;
'aria-invalid'?: 'true' | 'false' | boolean;
'aria-describedby'?: string;
}

const today = startOfDay(new Date());
const todayValue = format(today, 'M/d/yyyy');

export function DatePicker({
value,
onChange,
disabled = false,
error,
className,
placeholder = 'MM/DD/YYYY',
minDate,
defaultToToday = true,
compact = false,
onBlur,
'aria-invalid': ariaInvalid,
'aria-describedby': ariaDescribedby,
}: DatePickerProps) {
const [isOpen, setIsOpen] = useState(false);
const [inputValue, setInputValue] = useState(value);
const [month, setMonth] = useState<Date>(today);
const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
const containerRef = useRef<HTMLDivElement>(null);
const inputRef = useRef<HTMLInputElement>(null);
const dropdownRef = useRef<HTMLDivElement>(null);
const justSelectedRef = useRef(false);

const effectiveMinDate = minDate ?? today;

useEffect(() => {
setInputValue(value);
}, [value]);

const parseDate = (val: string): Date | undefined => {
if (!val) return undefined;
let d = parse(val, 'yyyy-MM-dd', new Date());
if (isValid(d)) return d;
d = parse(val, 'M/d/yyyy', new Date());
if (isValid(d)) return d;
d = parse(val, 'M/d/yy', new Date());
if (isValid(d)) return d;
return undefined;
};

const selectedDate = parseDate(inputValue);

// Position the dropdown relative to the input, rendered via portal
const updatePosition = useCallback(() => {
if (!inputRef.current) return;
const rect = inputRef.current.getBoundingClientRect();
const dropdownHeight = 320; // approximate calendar height
const spaceBelow = window.innerHeight - rect.bottom;
const openAbove = spaceBelow < dropdownHeight && rect.top > dropdownHeight;

setDropdownPos({
top: openAbove ? rect.top + window.scrollY - dropdownHeight - 4 : rect.bottom + window.scrollY + 4,
left: rect.left + window.scrollX,
});
}, []);

// Recalculate on open and on scroll/resize
useEffect(() => {
if (!isOpen) return;
updatePosition();

const handleScrollOrResize = () => updatePosition();
window.addEventListener('scroll', handleScrollOrResize, true);
window.addEventListener('resize', handleScrollOrResize);
return () => {
window.removeEventListener('scroll', handleScrollOrResize, true);
window.removeEventListener('resize', handleScrollOrResize);
};
}, [isOpen, updatePosition]);

// Close on outside click (check both container and portal dropdown)
useEffect(() => {
if (!isOpen) return;
const handleClickOutside = (e: MouseEvent) => {
const target = e.target as Node;
if (
containerRef.current?.contains(target) ||
dropdownRef.current?.contains(target)
) {
return;
}
setIsOpen(false);
};
document.addEventListener('mousedown', handleClickOutside);
return () => document.removeEventListener('mousedown', handleClickOutside);
}, [isOpen]);

  const handleInputFocus = () => {
    if (disabled) return;
    // Skip re-opening if focus returned after a day selection
    if (justSelectedRef.current) {
      justSelectedRef.current = false;
      return;
    }

    if (defaultToToday && !inputValue) {
      setInputValue(todayValue);
      onChange(todayValue);
    }
    const parsed = parseDate(defaultToToday && !inputValue ? todayValue : inputValue);
    setMonth(parsed ?? today);
    setIsOpen(true);
  };

  const openCalendar = () => {
    const parsed = parseDate(inputValue);
    setMonth(parsed ?? today);
    setIsOpen(true);
  };

const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
const val = e.target.value;
setInputValue(val);
onChange(val);
};

const handleDaySelect = (day: Date | undefined) => {
if (!day) {
  // Same date re-selected — DayPicker deselects it (returns undefined).
  // Just close the calendar and keep the existing value.
  setIsOpen(false);
  justSelectedRef.current = true;
  inputRef.current?.focus();
  return;
}
const formatted = format(day, 'M/d/yyyy');
setInputValue(formatted);
onChange(formatted);
setIsOpen(false);
justSelectedRef.current = true;
inputRef.current?.focus();
};

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
    } else if (e.key === 'Enter' && !isOpen) {
      openCalendar();
    }
  };

const calendarIcon = (
<svg
className="w-4 h-4 text-neutral-400"
fill="none"
stroke="currentColor"
viewBox="0 0 24 24"
aria-hidden="true"
>
<path
strokeLinecap="round"
strokeLinejoin="round"
strokeWidth={2}
d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
/>
</svg>
);

return (
<div ref={containerRef} className={cn('relative', className)}>
<div className="relative">
<input
ref={inputRef}
type="text"
value={inputValue}
onChange={handleInputChange}
onFocus={handleInputFocus}
onBlur={onBlur}
onKeyDown={handleKeyDown}
disabled={disabled}
placeholder={placeholder}
aria-label="Date"
aria-haspopup="dialog"
aria-expanded={isOpen}
aria-invalid={ariaInvalid}
aria-describedby={ariaDescribedby}
className={cn(
compact
? 'w-full px-2 py-1 pr-7 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-primary-500'
: 'flex h-10 w-full rounded-md border bg-white px-3 py-2 pr-9 text-sm placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:cursor-not-allowed disabled:opacity-50',
error ? 'border-red-300 bg-red-50' : 'border-neutral-200',
)}
/>
        <button
          type="button"
          tabIndex={-1}
          onClick={() => {
            if (disabled) return;
            if (isOpen) {
              setIsOpen(false);
              return;
            }
            openCalendar();
          }}
          disabled={disabled}
aria-label="Open calendar"
className={cn(
'absolute top-1/2 -translate-y-1/2 p-0.5 hover:text-neutral-600 disabled:opacity-50',
compact ? 'right-1' : 'right-2',
)}
>
{calendarIcon}
</button>
</div>

{error && !compact && (
<p className="mt-1 text-sm text-red-600">{error}</p>
)}

{isOpen && !disabled && createPortal(
<div
ref={dropdownRef}
role="dialog"
aria-label="Date picker"
className="fixed z-[9999] bg-white border border-neutral-200 rounded-lg p-2"
style={{
top: dropdownPos.top,
left: dropdownPos.left,
minWidth: '280px',
position: 'absolute',
}}
>
<DayPicker
mode="single"
selected={selectedDate}
onSelect={handleDaySelect}
month={month}
onMonthChange={setMonth}
disabled={{ before: effectiveMinDate }}
modifiersClassNames={{
selected: 'rdp-selected',
today: 'rdp-today',
disabled: 'rdp-disabled',
}}
/>
</div>,
document.body
)}
</div>
);
}

export default DatePicker;
